import React, { useState, useMemo } from 'react';
import Seo from '../components/Seo';
import { Flame, Plus, ShoppingBag, Check } from 'lucide-react';
import { PRODUCTS, SHOP_COLLECTIONS } from '../mock/mock';
import { useCart } from '../context/CartContext';
import Reveal from '../components/Reveal';
import MusicPlayer from '../components/MusicPlayer';

const Shop = () => {
  const [collection, setCollection] = useState('All');
  const { addItem } = useCart();
  const [added, setAdded] = useState(null);

  const filtered = useMemo(
    () => (collection === 'All' ? PRODUCTS : PRODUCTS.filter((p) => p.collection === collection)),
    [collection]
  );

  const handleAdd = (p) => {
    addItem(p, 1);
    setAdded(p.id);
    setTimeout(() => setAdded((cur) => (cur === p.id ? null : cur)), 1400);
  };

  return (
    <div className="bg-fire-ink">
      <Seo title="The Flame Shop" path="/shop" description="Wear your testimony. Shop the After the Fire™ Survivor & Firehouse collections, the novel, prayer journal and original soundtrack." />
      {/* Hero */}
      <section className="pt-32 pb-14 ember-texture text-center relative overflow-hidden">
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full opacity-20 blur-3xl" style={{ background: 'radial-gradient(circle, rgba(217,117,36,0.4), transparent 60%)' }} />
        <div className="max-w-3xl mx-auto px-6 relative">
          <Flame className="w-9 h-9 text-fire-gold mx-auto mb-5 animate-flicker" strokeWidth={1.4} />
          <span className="font-marcellus tracking-[0.25em] text-fire-gold text-xs">OFFICIAL ATF MERCH</span>
          <h1 className="font-display text-4xl md:text-6xl text-fire-cream mt-4">The Flame Shop</h1>
          <p className="font-cormorant italic text-fire-goldlight text-2xl mt-4">Wear Your Testimony.™</p>
          <p className="text-fire-cream/60 font-body mt-4 max-w-xl mx-auto">
            When you wear ATF, you’re not simply wearing a cute shirt — you’re wearing your testimony. Somebody will ask about it. And that conversation can become somebody else’s lifeline.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="sticky top-[74px] z-30 bg-fire-ink/95 backdrop-blur-md border-y border-fire-gold/12 py-4">
        <div className="max-w-[1300px] mx-auto px-5 lg:px-8 flex flex-wrap gap-3 justify-center">
          {SHOP_COLLECTIONS.map((c) => (
            <button
              key={c}
              onClick={() => setCollection(c)}
              className={`rounded-full px-5 py-2 font-body text-sm font-medium transition-all ${
                collection === c ? 'btn-gold' : 'border border-fire-gold/25 text-fire-cream/70 hover:text-fire-goldlight hover:border-fire-gold'
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      {/* Listen — soundtrack preview */}
      <section className="pt-12 -mb-4">
        <div className="max-w-[1000px] mx-auto px-5 lg:px-8">
          <Reveal>
            <MusicPlayer />
          </Reveal>
        </div>
      </section>

      {/* Grid */}
      <section className="py-16">
        <div className="max-w-[1300px] mx-auto px-5 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((p, i) => (
              <Reveal key={p.id} delay={(i % 3) * 60}>
                <div className="card-dark rounded-2xl overflow-hidden h-full flex flex-col hover-lift group">
                  <div className="relative aspect-square bg-fire-ink overflow-hidden">
                    <img src={p.img} alt={p.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    {p.featured && <span className="absolute top-3 left-3 bg-fire-gold text-fire-ink text-[10px] font-bold tracking-widest px-3 py-1 rounded-full">SIGNATURE</span>}
                    <span className="absolute top-3 right-3 bg-fire-ink/80 text-fire-goldlight text-[10px] font-body tracking-wide px-3 py-1 rounded-full border border-fire-gold/25">{p.collection}</span>
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <h3 className="font-display text-lg text-fire-cream leading-tight mb-2">{p.name}</h3>
                    <p className="text-fire-cream/55 font-body text-sm leading-relaxed mb-5 flex-1">{p.desc}</p>
                    <div className="flex items-center justify-between">
                      <span className="font-display text-2xl text-gradient-gold">${p.price}</span>
                      <button
                        onClick={() => handleAdd(p)}
                        className={`rounded-full px-5 py-2.5 font-semibold text-sm inline-flex items-center gap-2 transition-all ${
                          added === p.id ? 'bg-fire-maroon text-fire-cream' : 'btn-gold'
                        }`}
                      >
                        {added === p.id ? (<><Check className="w-4 h-4" /> Added</>) : (<><Plus className="w-4 h-4" /> Add</>)}
                      </button>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Movement note */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-fire-maroondark via-fire-ink to-fire-maroondark" />
        <div className="relative max-w-[800px] mx-auto px-6 text-center">
          <ShoppingBag className="w-9 h-9 text-fire-gold mx-auto mb-5" strokeWidth={1.4} />
          <p className="font-cormorant text-2xl md:text-3xl text-fire-cream italic leading-relaxed">
            “Thank you. Girl, you don’t even know what I survived.”
          </p>
          <p className="font-marcellus tracking-[0.25em] text-fire-goldlight text-xs mt-6">THANK YOU FOR SUPPORTING THE MOVEMENT</p>
        </div>
      </section>
    </div>
  );
};

export default Shop;
